import { Component, EventEmitter, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SVGIcon, xIcon } from '@progress/kendo-svg-icons';
import { IconsModule } from '@progress/kendo-angular-icons';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { NotificationService } from '@progress/kendo-angular-notification';
import { BaseComponent } from 'src/app/core/shared/common/base.component';

/**
 * Request Compliance Popup Component
 * Allows users to request compliance frameworks that aren't currently available
 */
@Component({
  selector: 'app-request-compliance-popup',
  templateUrl: './request-compliance-popup.component.html',
  styleUrls: ['./request-compliance-popup.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonsModule, IconsModule],
})
export class RequestCompliancePopupComponent extends BaseComponent {
  @Output() close = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private notificationService = inject(NotificationService);

  public xIcon: SVGIcon = xIcon;
  public requestForm!: FormGroup;
  public isSubmitting = false;

  ngOnInit(): void {
    this.initializeForm();
  }

  /**
   * Initialize the request form
   */
  private initializeForm(): void {
    this.requestForm = this.fb.group({
      complianceFramework: ['', [Validators.required, Validators.minLength(2)]],
      additionalDetails: [''],
    });
  }

  /**
   * Handle form submission
   */
  public onSubmit(): void {
    if (this.requestForm.invalid) {
      this.requestForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const formValue = this.requestForm.value;

    // Simulate submission delay (in real app, this would be an API call)
    setTimeout(() => {
      this.notificationService.show({
        content: `Compliance framework request submitted: "${formValue.complianceFramework}". Our team will review your request and add it to the system.`,
        cssClass: 'success-notification',
        animation: { type: 'fade', duration: 400 },
        position: { horizontal: 'center', vertical: 'top' },
        type: { style: 'success', icon: true },
        hideAfter: 6000,
      });

      console.log('[Mock] Compliance framework request:', formValue);
      this.isSubmitting = false;
      this.onClose();
    }, 500);
  }

  /**
   * Handle close event
   */
  public onClose(): void {
    this.close.emit();
  }

  /**
   * Check if form field has error
   */
  public hasError(fieldName: string): boolean {
    const field = this.requestForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  /**
   * Get error message for field
   */
  public getErrorMessage(fieldName: string): string {
    const field = this.requestForm.get(fieldName);
    if (field?.hasError('required')) {
      return 'This field is required';
    }
    if (field?.hasError('minlength')) {
      return 'Please enter at least 2 characters';
    }
    return '';
  }
}
